import React, { useId } from 'react';
import { useTranslation } from 'localization';
import { RadioButton } from 'primereact/radiobutton';
import type { ForkPolicy } from 'types/channel';
import './ForkPolicyRadioGroupAtom.scss';

export interface ForkPolicyRadioGroupAtomProps {
  value: ForkPolicy;
  onChange: (policy: ForkPolicy) => void;
  disabled?: boolean;
  className?: string;
  name?: string;
}

interface PolicyOption {
  policy: ForkPolicy;
  labelKey: string;
  hintKey: string;
}

const OPTIONS: PolicyOption[] = [
  {
    policy: 'anyone',
    labelKey: 'marketplace.itemDetail.permissions.forkPolicyAnyone',
    hintKey: 'marketplace.itemDetail.permissions.forkPolicyHint.anyone',
  },
  {
    policy: 'subscribers',
    labelKey: 'marketplace.itemDetail.permissions.forkPolicySubscribers',
    hintKey: 'marketplace.itemDetail.permissions.forkPolicyHint.subscribers',
  },
  {
    policy: 'granted',
    labelKey: 'marketplace.itemDetail.permissions.forkPolicyGranted',
    hintKey: 'marketplace.itemDetail.permissions.forkPolicyHint.granted',
  },
];

const ForkPolicyRadioGroupAtom: React.FC<ForkPolicyRadioGroupAtomProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
  name,
}) => {
  const { t } = useTranslation();

  const groupId = useId();
  const groupName = name ?? groupId;
  const groupLabelId = `${groupId}-label`;

  const classes = ['marketplace-fork-policy-radio-group', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      role="radiogroup"
      aria-labelledby={groupLabelId}
    >
      <span id={groupLabelId} className="marketplace-fork-policy-radio-group__title">
        {t('marketplace.itemDetail.permissions.forkPolicy')}
      </span>
      {OPTIONS.map((option) => {
        const inputId = `${groupId}-${option.policy}`;
        const hintId = `${inputId}-hint`;
        return (
          <div
            key={option.policy}
            className="marketplace-fork-policy-radio-group__option"
          >
            <RadioButton
              inputId={inputId}
              name={groupName}
              value={option.policy}
              checked={value === option.policy}
              disabled={disabled}
              onChange={() => onChange(option.policy)}
              aria-describedby={hintId}
            />
            <label
              htmlFor={inputId}
              className="marketplace-fork-policy-radio-group__label"
            >
              <span className="marketplace-fork-policy-radio-group__label-text">
                {t(option.labelKey)}
              </span>
              <span
                id={hintId}
                className="marketplace-fork-policy-radio-group__hint"
              >
                {t(option.hintKey)}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
};

ForkPolicyRadioGroupAtom.displayName = 'ForkPolicyRadioGroupAtom';

export default ForkPolicyRadioGroupAtom;
